import React, { useEffect, useMemo, useState } from 'react'
import { useLocation, useParams, Link } from 'react-router-dom'
import { Anchor, Box, Group, Text } from '@mantine/core'
import { useSelector } from 'react-redux'
import { getOrgBillingAlerts } from '../services/actions/orgActions'

const severityRank = {
	critical: 3,
	warning: 2,
	info: 1,
}

const MESSAGING_PAYMENT_METHOD_REQUIRED_MESSAGE =
	'Messaging is disabled until a default payment method is added.'

const formatDateLabel = (dateTime) => {
	if (!dateTime) return null
	const parsed = new Date(dateTime)
	if (Number.isNaN(parsed.getTime())) return null
	return new Intl.DateTimeFormat('en-US', {
		timeZone: 'UTC',
		year: 'numeric',
		month: 'short',
		day: 'numeric',
	}).format(parsed)
}

const BillingAlertFooter = ({ embedded = false }) => {
	const { orgSlug } = useParams()
	const location = useLocation()
	const orgRole = useSelector((state) => state.org.orgRole)
	const userType = useSelector((state) => state.user.authUser?.userType)
	const [alertPayload, setAlertPayload] = useState(null)
	const isMessagingRoute = /\/(messaging|messagesetups|messagingsetups)(\/|$)/.test(
		location.pathname.toLowerCase(),
	)

	const inControlRoutes = location.pathname.startsWith('/control')
	const canViewBillingLink = orgRole === 'admin' || userType === 'superAdmin'

	useEffect(() => {
		let cancelled = false
		let intervalId = null

		if (!orgSlug || inControlRoutes) {
			setAlertPayload(null)
			return () => {
				cancelled = true
				if (intervalId) clearInterval(intervalId)
			}
		}

		const loadAlerts = async () => {
			try {
				const payload = await getOrgBillingAlerts(orgSlug)
				if (!cancelled) setAlertPayload(payload)
			} catch (error) {
				if (!cancelled) setAlertPayload(null)
			}
		}

		loadAlerts()
		intervalId = setInterval(loadAlerts, 60000)

		return () => {
			cancelled = true
			if (intervalId) clearInterval(intervalId)
		}
	}, [orgSlug, inControlRoutes])

	const primaryAlert = useMemo(() => {
		const alerts = Array.isArray(alertPayload?.alerts) ? alertPayload.alerts : []
		if (alerts.length === 0) return null
		const routeScopedAlerts = alerts.filter((alert) => {
			const isMessagingPaymentMethodAlert =
				alert?.key === 'messaging_payment_method_required' ||
				alert?.message === MESSAGING_PAYMENT_METHOD_REQUIRED_MESSAGE
			return isMessagingRoute || !isMessagingPaymentMethodAlert
		})
		if (routeScopedAlerts.length === 0) return null

		return routeScopedAlerts
			.slice()
			.sort((a, b) => (severityRank[b?.severity] || 0) - (severityRank[a?.severity] || 0))[0]
	}, [alertPayload, isMessagingRoute])

	if (!primaryAlert) return null

	const suspensionDateLabel = formatDateLabel(primaryAlert.suspensionDateUtc)
	const bgColor =
		primaryAlert.severity === 'critical'
			? '#fff5f5'
			: primaryAlert.severity === 'warning'
			? '#fff9db'
			: '#f4f6ff'
	const borderColor =
		primaryAlert.severity === 'critical'
			? '#ffa8a8'
			: primaryAlert.severity === 'warning'
			? '#ffd43b'
			: '#a5d8ff'

	return (
		<Box
			px='md'
			py='xs'
			sx={{
				position: embedded ? 'static' : 'fixed',
				bottom: 0,
				left: 0,
				right: 0,
				zIndex: 200,
				backgroundColor: bgColor,
				color: '#343a40',
				borderTop: `1px solid ${borderColor}`,
			}}
		>
			<Group spacing='xs' position='apart'>
				<Text size='sm'>
					{primaryAlert.message}
					{suspensionDateLabel
						? ` Suspension date (UTC): ${suspensionDateLabel}.`
						: ''}
				</Text>
				{canViewBillingLink && primaryAlert.actionPath && (
					<Anchor component={Link} to={primaryAlert.actionPath} size='sm' fw={600}>
						Open Plan & Billing
					</Anchor>
				)}
			</Group>
		</Box>
	)
}

export default BillingAlertFooter
